import { ChangeDetectionStrategy, Component, ElementRef, Inject, Input, OnInit, Optional, forwardRef, signal } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { isValidPhoneNumber } from 'libphonenumber-js';

/**
 * Mobile number input (CVA): digits + spaces only (optional leading +),
 * numeric keyboard on touch devices, validated with libphonenumber-js against
 * the configured default country (IN). Model value is the cleaned string.
 *
 * <app-mobile-input formControlName="mobile"></app-mobile-input>
 * <app-mobile-input class="form-control form-control-sm" ...>  <!-- small, like a native input -->
 */
@Component({
    selector: 'app-mobile-input',
    templateUrl: './app-mobile-input.component.html',
    styleUrls: ['./app-mobile-input.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppMobileInputComponent), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => AppMobileInputComponent), multi: true }
    ]
})
export class AppMobileInputComponent implements ControlValueAccessor, Validator, OnInit {
    @Input() placeholder = 'Mobile number';
    /** ISO country used when the number has no +prefix. */
    @Input() country = 'IN';
    @Input() required = false;
    @Input() tabindex: string | number | null = null;
    @Input() inputId = '';
    /** Compact (Bootstrap form-control-sm) sizing; also set by a
     *  form-control-sm class on the HOST (detected in ngOnInit). */
    @Input() small = false;

    readonly display = signal('');
    readonly disabled = signal(false);

    private onChange: (v: string) => void = () => { };
    private onTouched: () => void = () => { };

    // Optional + defaulted so the unit spec's bare `new AppMobileInputComponent()`
    // (no TestBed/injection context) keeps working.
    constructor(@Optional() @Inject(ElementRef) private readonly elementRef: ElementRef | null = null) { }

    ngOnInit(): void {
        // class="form-control form-control-sm" on the HOST mirrors a native
        // small input: the :host css strips the host's own Bootstrap box (the
        // inner input is the visible form-control) and the -sm marker flips
        // the inner input to form-control-sm.
        const hostClasses = (this.elementRef?.nativeElement as HTMLElement | undefined)?.classList;
        if (hostClasses && hostClasses.contains('form-control-sm')) {
            this.small = true;
        }
    }

    writeValue(v: any): void {
        this.display.set(v ? String(v) : '');
    }

    registerOnChange(fn: any): void { this.onChange = fn; }
    registerOnTouched(fn: any): void { this.onTouched = fn; }
    setDisabledState(isDisabled: boolean): void { this.disabled.set(isDisabled); }

    validate(control: AbstractControl): ValidationErrors | null {
        const v: string = control.value || '';
        if (!v) {
            return this.required ? { required: true } : null;
        }
        try {
            return isValidPhoneNumber(v, this.country as any) ? null : { mobile: true };
        } catch {
            return { mobile: true };
        }
    }

    /**
     * Accepts either the raw string (unit tests) or the DOM `input` event. The
     * event form matters: rejecting a character leaves `display` UNCHANGED, so
     * the `[value]` binding has nothing to write and the rejected character
     * stays visible in the box. The element therefore has to be corrected
     * directly — with the caret put back where the typing was.
     */
    onInput(source: string | Event) {
        const input = typeof source === 'string' ? null : (source.target as HTMLInputElement | null);
        const raw = typeof source === 'string' ? source : (input?.value ?? '');
        // one optional leading +, then digits and spaces only (spaces are allowed
        // for readable grouping e.g. "98765 43210"; letters/special chars rejected).
        // libphonenumber ignores the spaces when validating. Hard cap at 20
        // characters — person.MOBILE_PHONE is varchar(20) (R80b); the template's
        // native maxlength=20 covers typing, this slice covers a paste whose
        // rejected characters would otherwise leave >20 digits behind.
        const cleaned = ((raw.startsWith('+') ? '+' : '') + raw.replace(/[^\d ]/g, '')).slice(0, 20);
        if (input && input.value !== cleaned) {
            const caret = input.selectionStart ?? cleaned.length;
            input.value = cleaned;
            const moved = Math.max(0, caret - (raw.length - cleaned.length));
            try {
                input.setSelectionRange(moved, moved);
            } catch {
                // type=tel does not support selection on every browser
            }
        }
        this.display.set(cleaned);
        this.onChange(cleaned);
    }

    onBlur() {
        this.onTouched();
    }

    /**
     * Enter advances to the next focusable field (form-flow parity with the
     * other keyboard-driven inputs) rather than submitting the form. Reads the
     * input straight off the event (no ElementRef) so the component stays
     * plain-`new`-constructable in unit tests.
     */
    onEnter(event: Event) {
        event.preventDefault();
        const input = event.target as HTMLElement | null;
        if (!input || typeof document === 'undefined') {
            return;
        }
        const focusable = Array.from(
            document.querySelectorAll<HTMLElement>('input, select, textarea, button, a[href], [tabindex]'))
            .filter(el => el.tabIndex !== -1
                && !(el as HTMLInputElement).disabled
                && el.getClientRects().length > 0);
        const here = focusable.indexOf(input);
        if (here !== -1 && focusable[here + 1]) {
            focusable[here + 1].focus();
        }
    }
}
